"use client";

import { track } from "@vercel/analytics";

import {
  getPublicContactPhone,
  getPublicContactPhoneDisplay,
  getWhatsAppHref,
} from "@/lib/site";

/**
 * "WhatsApp us" and "Call us", as real buttons (Tom, 2026-10-11).
 *
 * Both go to the main line (020 4587 1648), the same number the footer, /book and the Organization
 * structured data print, because every one of them reads `getPublicContactPhone`. A second number
 * on one surface is a number somebody rings and nobody answers.
 *
 * PLACED WHERE SOMEBODY IS DECIDING HOW TO GET IN TOUCH, NOT EVERYWHERE: /book, the footer's contact
 * block and the phone booking bar on /before-we-talk. A floating chat bubble on every page was
 * considered and left out: on a phone it sits over body copy and the cookie banner, and on this
 * site the booking bar already owns the bottom of the screen where it appears.
 *
 * CLICKS ARE COUNTED WITH VERCEL ANALYTICS, which sets no cookies and so needs no consent (the same
 * `track` every form here uses). The event carries the method and where on the page it was, never
 * anything the visitor typed. Meta and Google Analytics hear about the tap too, but only after
 * Accept, from the site-wide link listener in ReferralTracker (trackContactClick in ad-tags.ts).
 */

type Placement = "book" | "footer" | "sticky-bar" | "get-results";

function recordClick(method: "whatsapp" | "call", placement: Placement) {
  track("contact-click", { method, placement });
}

function WhatsAppGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false" className={className} fill="currentColor">
      <path d="M12.04 2C6.58 2 2.14 6.43 2.14 11.9c0 1.75.46 3.45 1.33 4.95L2 22l5.29-1.39a9.86 9.86 0 0 0 4.75 1.21h.01c5.46 0 9.9-4.43 9.9-9.9a9.83 9.83 0 0 0-2.9-7A9.83 9.83 0 0 0 12.04 2Zm0 18.15h-.01a8.22 8.22 0 0 1-4.19-1.15l-.3-.18-3.14.82.84-3.06-.2-.31a8.2 8.2 0 0 1-1.26-4.37c0-4.54 3.7-8.24 8.25-8.24a8.2 8.2 0 0 1 5.83 2.42 8.18 8.18 0 0 1 2.41 5.83c0 4.55-3.7 8.24-8.23 8.24Zm4.52-6.17c-.25-.12-1.47-.72-1.69-.8-.23-.09-.39-.13-.56.12-.16.25-.64.8-.78.97-.15.16-.29.18-.54.06-.25-.12-1.04-.38-1.99-1.23-.73-.66-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42h-.48a.92.92 0 0 0-.66.31c-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.22-.16-.47-.29Z" />
    </svg>
  );
}

function PhoneGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 16.9v2.6a1.8 1.8 0 0 1-2 1.8 17.8 17.8 0 0 1-7.8-2.8 17.5 17.5 0 0 1-5.4-5.4A17.8 17.8 0 0 1 3 5.3a1.8 1.8 0 0 1 1.8-2h2.6a1.8 1.8 0 0 1 1.8 1.6c.1.9.3 1.7.6 2.5a1.8 1.8 0 0 1-.4 1.9L8.3 10.4a14.4 14.4 0 0 0 5.4 5.4l1.1-1.1a1.8 1.8 0 0 1 1.9-.4c.8.3 1.6.5 2.5.6a1.8 1.8 0 0 1 1.6 1.8Z" />
    </svg>
  );
}

/* The site's one button height (h-12) and the footer's secondary pill, so these read as the same
   family as "Book a call" rather than as a widget dropped in from elsewhere. */
const PILL =
  "ownly-control inline-flex h-12 items-center justify-center gap-2.5 rounded-full border px-6 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent";

/**
 * The two buttons side by side. `showNumber` writes the number into the call button, for places
 * where the number itself is the thing somebody came to find (/book).
 */
export function ContactButtons({
  placement,
  showNumber = false,
  className = "",
}: {
  placement: Exclude<Placement, "sticky-bar">;
  showNumber?: boolean;
  className?: string;
}) {
  const phone = getPublicContactPhone();
  const display = getPublicContactPhoneDisplay();
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a
        href={getWhatsAppHref()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => recordClick("whatsapp", placement)}
        className={`${PILL} border-[#25D366]/45 bg-[#25D366]/[0.08] text-white hover:border-[#25D366]/80 hover:bg-[#25D366]/[0.14]`}
      >
        <WhatsAppGlyph className="size-[18px] text-[#25D366]" />
        WhatsApp us
        <span className="sr-only"> (opens WhatsApp)</span>
      </a>
      <a
        href={`tel:${phone}`}
        onClick={() => recordClick("call", placement)}
        className={`${PILL} border-white/[0.16] text-white/85 hover:border-white/30 hover:text-white`}
      >
        <PhoneGlyph className="size-[17px] text-ownly-accent-text" />
        {showNumber ? (
          <>
            Call us <span className="font-normal text-white/65">{display}</span>
          </>
        ) : (
          <>
            Call us<span className="sr-only"> on {display}</span>
          </>
        )}
      </a>
    </div>
  );
}

/**
 * The same two actions as round icon buttons, for the phone booking bar, where the booking button
 * keeps the width and these sit beside it. 48px, so they clear the 24px target minimum twice over
 * and match the bar's own button height.
 */
export function ContactIconButtons({ tabbable }: { tabbable: boolean }) {
  const phone = getPublicContactPhone();
  const display = getPublicContactPhoneDisplay();
  const ROUND =
    "inline-flex size-12 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent";
  return (
    <>
      <a
        href={getWhatsAppHref()}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={tabbable ? 0 : -1}
        aria-label="WhatsApp us (opens WhatsApp)"
        onClick={() => recordClick("whatsapp", "sticky-bar")}
        className={`${ROUND} border-[#25D366]/45 bg-[#25D366]/[0.1] text-[#25D366]`}
      >
        <WhatsAppGlyph className="size-[22px]" />
      </a>
      <a
        href={`tel:${phone}`}
        tabIndex={tabbable ? 0 : -1}
        aria-label={`Call us on ${display}`}
        onClick={() => recordClick("call", "sticky-bar")}
        className={`${ROUND} border-white/[0.18] bg-white/[0.04] text-white/90`}
      >
        <PhoneGlyph className="size-[20px]" />
      </a>
    </>
  );
}

/**
 * A plain text link for the footer's business-card block, where the other rows (phone, email) are
 * text links too. Counted the same way as the buttons.
 */
export function ContactTextLink({
  method,
  className,
  children,
  placement = "footer",
}: {
  method: "whatsapp" | "call";
  className?: string;
  children: React.ReactNode;
  placement?: Placement;
}) {
  const whatsapp = method === "whatsapp";
  return (
    <a
      href={whatsapp ? getWhatsAppHref() : `tel:${getPublicContactPhone()}`}
      {...(whatsapp ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      onClick={() => recordClick(method, placement)}
      className={className}
    >
      {children}
      {whatsapp ? <span className="sr-only"> (opens WhatsApp)</span> : null}
    </a>
  );
}
